/**
 * Localization strings used in the Schema Config and data model viewer
 *
 * @module
 */

import { createDictionary } from './utils';

// Refer to "Guidelines for Programmers" in ./README.md before editing this file

export const schemaText = createDictionary({
  table: {
    'en-us': 'Table',
    'ru-ru': 'Стол',
    'es-es': 'Tabla',
    'fr-fr': 'Tableau',
    'uk-ua': 'Таблиця',
    'de-ch': 'Tabelle',
    'pt-br': 'Mesa',
  },
  tables: {
    'en-us': 'Tables',
    'ru-ru': 'Таблицы',
    'es-es': 'Tablas',
    'fr-fr': 'Tables',
    'uk-ua': 'Столи',
    'de-ch': 'Tabellen',
    'pt-br': 'Tabelas',
  },
  tableName: {
    'en-us': 'Table Name',
    'ru-ru': 'Название таблицы',
    'fr-fr': 'Nom de la table',
    'uk-ua': 'Назва таблиці',
    'de-ch': 'Tabellennamen',
    'es-es': 'Nombre de la tabla',
    'pt-br': 'Nome da tabela',
  },
  withoutTable: {
    'en-us': 'Without Table',
    'de-ch': 'Ohne Tisch',
    'es-es': 'Sin tabla',
    'fr-fr': 'Sans table',
    'ru-ru': 'Без стола',
    'uk-ua': 'Без столу',
    'pt-br': 'Sem mesa',
  },
  schemaConfig: {
    'en-us': 'Schema Config',
    'ru-ru': 'Конфигурация схемы',
    'es-es': 'Configuración del esquema',
    'fr-fr': 'Configuration du schéma',
    'uk-ua': 'Конфігурація схеми',
    'de-ch': 'Schema konfigurieren',
    'pt-br': 'Configuração do esquema',
  },
  unsavedSchemaUnloadProtect: {
    'en-us': 'Schema changes have not been saved',
    'ru-ru': 'Изменения схемы не были сохранены',
    'es-es': 'Los cambios en el esquema no se han guardado',
    'fr-fr': "Les modifications de schéma n'ont pas été enregistrées.",
    'uk-ua': 'Зміни схеми не збережено',
    'de-ch': 'Schema Änderungen wurden nicht gespeichert',
    'pt-br': 'As alterações de esquema não foram salvas.',
  },
  changeBaseTable: {
    'en-us': 'Change Base Table',
    'ru-ru': 'Изменить базовую таблицу',
    'es-es': 'Cambiar la tabla base',
    'fr-fr': 'Modifier la table de base',
    'uk-ua': 'Змінити базову таблицю',
    'de-ch': 'Basis-Tabelle ändern',
    'pt-br': 'Alterar tabela base',
  },
  field: {
    'en-us': 'Field',
    'ru-ru': 'Поле',
    'es-es': 'Campo',
    'fr-fr': 'Champ',
    'uk-ua': 'Поле',
    'de-ch': 'Feld',
    'pt-br': 'Campo',
  },
  fields: {
    'en-us': 'Fields',
    'ru-ru': 'Поля',
    'es-es': 'Campos',
    'fr-fr': 'Champs',
    'uk-ua': 'Поля',
    'de-ch': 'Felder',
    'pt-br': 'Campos',
  },
  relationships: {
    'en-us': 'Relationships',
    'ru-ru': 'Отношения',
    'es-es': 'Relaciones',
    'fr-fr': 'Relations',
    'uk-ua': 'Стосунки',
    'de-ch': 'Beziehungen',
    'pt-br': 'Relacionamentos',
  },
  database: {
    'en-us': 'Database',
    'de-ch': 'Datenbank',
    'fr-fr': 'Base de données',
    'ru-ru': 'База данных',
    'uk-ua': 'База даних',
    'es-es': 'Base de datos',
    'pt-br': 'Banco de dados',
  },
  setScope: {
    'en-us': 'Set Scope',
    'de-ch': 'Zielbereich einstellen',
    'es-es': 'Establecer alcance',
    'fr-fr': 'Définir le périmètre',
    'ru-ru': 'Установить область действия',
    'uk-ua': 'Встановити область дії',
    'pt-br': 'Definir Escopo',
  },
  caption: {
    'en-us': 'Caption',
    'ru-ru': 'Подпись',
    'es-es': 'Subtítulo',
    'fr-fr': 'Légende',
    'uk-ua': 'Підпис',
    'de-ch': 'Beschriftung',
    'pt-br': 'Rubrica',
  },
  description: {
    'en-us': 'Description',
    'ru-ru': 'Описание',
    'es-es': 'Descripción',
    'fr-fr': 'Description',
    'uk-ua': 'Опис',
    'de-ch': 'Beschreibung',
    'pt-br': 'Descrição',
  },
  hideTable: {
    'en-us': 'Hide Table',
    'ru-ru': 'Скрыть таблицу',
    'es-es': 'Ocultar tabla',
    'fr-fr': 'Masquer le tableau',
    'uk-ua': 'Приховати таблицю',
    'de-ch': 'Tabelle verbergen',
    'pt-br': 'Ocultar tabela',
  },
  hideField: {
    'en-us': 'Hide Field',
    'ru-ru': 'Скрыть поле',
    'es-es': 'Ocultar campo',
    'fr-fr': 'Masquer le champ',
    'uk-ua': 'Приховати поле',
    'de-ch': 'Feld verbergen',
    'pt-br': 'Ocultar campo',
  },
  tableFormat: {
    'en-us': 'Table Format',
    'ru-ru': 'Формат таблицы',
    'es-es': 'Formato de tabla',
    'fr-fr': 'Format de tableau',
    'uk-ua': 'Формат таблиці',
    'de-ch': 'Tabellenformat',
    'pt-br': 'Formato de tabela',
  },
  tableAggregation: {
    'en-us': 'Table Aggregation',
    'ru-ru': 'Агрегация таблиц',
    'es-es': 'Agregación de tablas',
    'fr-fr': 'Agrégation de tableaux',
    'uk-ua': 'Агрегація таблиць',
    'de-ch': 'Tabellenaggregation',
    'pt-br': 'Agregação de tabelas',
  },
  oneToOne: {
    'en-us': 'One-to-one',
    'ru-ru': 'Один на один',
    'es-es': 'Uno-a-uno',
    'fr-fr': 'En tête-à-tête',
    'uk-ua': 'Один на один',
    'de-ch': 'Eins zu eins',
    'pt-br': 'Um para um',
  },
  oneToMany: {
    'en-us': 'One-to-many',
    'ru-ru': 'Один ко многим',
    'es-es': 'Uno a muchos',
    'fr-fr': 'Un à plusieurs',
    'uk-ua': 'Один до багатьох',
    'de-ch': 'Eins zu vielen',
    'pt-br': 'Um para muitos',
  },
  manyToOne: {
    'en-us': 'Many-to-one',
    'ru-ru': 'Многие-к-одному',
    'es-es': 'Muchos a uno',
    'fr-fr': 'Plusieurs vers un',
    'uk-ua': 'Багато до одного',
    'de-ch': 'Viele zu eins',
    'pt-br': 'Muitos para um',
  },
  manyToMany: {
    'en-us': 'many-to-many',
    'ru-ru': 'многие-ко-многим',
    'es-es': 'muchos a muchos',
    'fr-fr': 'plusieurs à plusieurs',
    'uk-ua': 'багато-до-багатьох',
    'de-ch': 'Viele zu viele',
    'pt-br': 'muitos para muitos',
  },
  fieldLength: {
    'en-us': 'Length',
    'es-es': 'Longitud',
    'fr-fr': 'Longueur',
    'uk-ua': 'Довжина',
    'de-ch': 'Länge',
    'ru-ru': 'Длина',
    'pt-br': 'Comprimento',
  },
  readOnly: {
    'en-us': 'Read-only',
    'ru-ru': 'Только для чтения',
    'es-es': 'Sólo lectura',
    'fr-fr': 'Lecture seule',
    'uk-ua': 'Тільки для читання',
    'de-ch': 'Nur-Lesen',
    'pt-br': 'Somente leitura',
  },
  fieldFormat: {
    'en-us': 'Field Format',
    'ru-ru': 'Формат поля',
    'es-es': 'Formato de campo',
    'fr-fr': 'Format du champ',
    'uk-ua': 'Формат поля',
    'de-ch': 'Feldformat',
    'pt-br': 'Formato do campo',
  },
  uiFormattersForField: {
    'en-us': 'Field Formatters for {fieldLabel:string}',
    'de-ch': 'Feldformatierer für {fieldLabel:string}',
    'es-es': 'Formateadores de campo para {fieldLabel:string}',
    'fr-fr': 'Formateurs de champs pour {fieldLabel:string}',
    'pt-br': 'Formatadores de campo para {fieldLabel:string}',
    'ru-ru': 'Форматировщики полей для {fieldLabel:string}',
    'uk-ua': 'Форматування полів для {fieldLabel:string}',
  },
  formatted: {
    'en-us': 'Formatted',
    'ru-ru': 'Отформатированный',
    'es-es': 'Formateado',
    'fr-fr': 'Formaté',
    'uk-ua': 'Відформатовано',
    'de-ch': 'Formatiert',
    'pt-br': 'Formatado',
  },
  webLink: {
    'en-us': 'Web Link',
    'ru-ru': 'Веб-ссылка',
    'es-es': 'Enlace web',
    'fr-fr': 'Lien Web',
    'uk-ua': 'Веб-посиланні',
    'de-ch': 'Web-Link',
    'pt-br': 'Link da Web',
  },
  userDefined: {
    'en-us': 'User Defined',
    'es-es': 'Definido por el usuario',
    'fr-fr': "Défini par l'utilisateur",
    'uk-ua': 'Визначено користувачем',
    'de-ch': 'Benutzerdefiniert',
    'ru-ru': 'Определяется пользователем',
    'pt-br': 'Definido pelo usuário',
  },
  addLanguage: {
    'en-us': 'Add Language',
    'fr-fr': 'Ajouter une langue',
    'uk-ua': 'Додати мову',
    'de-ch': 'Sprache hinzuzufügen',
    'es-es': 'Agregar idioma',
    'ru-ru': 'Добавить язык',
    'pt-br': 'Adicionar idioma',
  },
  databaseColumn: {
    'en-us': 'Database Column',
    'ru-ru': 'Столбец базы данных',
    'es-es': 'Columna de base de datos',
    'fr-fr': 'Colonne de base de données',
    'uk-ua': 'Стовпець бази даних',
    'de-ch': 'Datenbank-Spalte',
    'pt-br': 'Coluna do banco de dados',
  },
  relatedTable: {
    'en-us': 'Related Model',
    'ru-ru': 'Связанная модель',
    'es-es': 'Modelo relacionado',
    'fr-fr': 'Modèle associé',
    'uk-ua': "Пов'язана модель",
    'de-ch': 'Zugehöriges Modell',
    'pt-br': 'Modelo relacionado',
  },
  otherSideName: {
    'en-us': 'Other side name',
    'ru-ru': 'Имя другой стороны',
    'fr-fr': "Nom de l'autre côté",
    'uk-ua': 'Інша назва сторони',
    'de-ch': 'Name der anderen Seite',
    'es-es': 'Otro nombre lateral',
    'pt-br': 'Outro nome do lado',
  },
  dependent: {
    'en-us': 'Dependent',
    'ru-ru': 'Зависимый',
    'es-es': 'Dependiente',
    'fr-fr': 'Dépendant',
    'uk-ua': 'Утриманець',
    'de-ch': 'Abhängig',
    'pt-br': 'Dependente',
  },
  independent: {
    'en-us': 'Independent',
    'es-es': 'Independiente',
    'fr-fr': 'Indépendant',
    'ru-ru': 'Независимый',
    'uk-ua': 'Незалежний',
    'de-ch': 'Unabhängig',
    'pt-br': 'Independente',
  },
  downloadAsJson: {
    'en-us': 'Download as JSON',
    'ru-ru': 'Скачать как JSON',
    'es-es': 'Descargar como JSON',
    'fr-fr': 'Télécharger au format JSON',
    'uk-ua': 'Завантажити як JSON',
    'de-ch': 'Als JSON herunterladen',
    'pt-br': 'Baixar como JSON',
  },
  downloadAsXml: {
    'en-us': 'Download as XML',
    'ru-ru': 'Загрузить как XML',
    'es-es': 'Descargar como XML',
    'fr-fr': 'Télécharger au format XML',
    'uk-ua': 'Завантажити як XML',
    'de-ch': 'Als XML herunterladen',
    'pt-br': 'Baixar como XML',
  },
  downloadAsTsv: {
    'en-us': 'Download as TSV',
    'ru-ru': 'Загрузить как TSV',
    'es-es': 'Descargar como TSV',
    'fr-fr': 'Télécharger au format TSV',
    'uk-ua': 'Завантажити як TSV',
    'de-ch': 'Als TSV herunterladen',
    'pt-br': 'Baixar como TSV',
  },
  tableId: {
    'en-us': 'Table ID',
    'ru-ru': 'Идентификатор таблицы',
    'es-es': 'ID de tabla',
    'fr-fr': 'ID de la table',
    'uk-ua': 'Ідентифікатор таблиці',
    'de-ch': 'Tabellen-ID',
    'pt-br': 'ID da tabela',
  },
  fieldCount: {
    'en-us': 'Field count',
    'ru-ru': 'Количество полей',
    'es-es': 'Conteo de campos',
    'fr-fr': 'Nombre de champs',
    'uk-ua': 'Кількість полів',
    'de-ch': 'Anzahl Felder',
    'pt-br': 'Contagem de campos',
  },
  relationshipCount: {
    'en-us': 'Relationship count',
    'ru-ru': 'Количество отношений',
    'es-es': 'Recuento de relaciones',
    'fr-fr': 'Nombre de relations',
    'uk-ua': "Кількість зв'язків",
    'de-ch': 'Anzahl Beziehungen',
    'pt-br': 'Contagem de relacionamentos',
  },
  databaseSchema: {
    'en-us': 'Database Schema',
    'ru-ru': 'Схема базы данных',
    'es-es': 'Esquema de base de datos',
    'fr-fr': 'Schéma de base de données',
    'uk-ua': 'Схема бази даних',
    'de-ch': 'Datenbankschema',
    'pt-br': 'Esquema do banco de dados',
  },
  selectedTables: {
    'en-us': 'Selected Tables',
    'ru-ru': 'Избранные таблицы',
    'es-es': 'Tablas seleccionadas',
    'fr-fr': 'Tableaux sélectionnés',
    'uk-ua': 'Вибрані таблиці',
    'de-ch': 'Ausgewählte Tabellen',
    'pt-br': 'Tabelas selecionadas',
  },
  possibleTables: {
    'en-us': 'Possible Tables',
    'ru-ru': 'Возможные таблицы',
    'es-es': 'Tablas posibles',
    'fr-fr': 'Tables possibles',
    'uk-ua': 'Можливі таблиці',
    'de-ch': 'Mögliche Tabellen',
    'pt-br': 'Tabelas possíveis',
  },
  goToTop: {
    'en-us': 'Go to top',
    'es-es': 'Volver al inicio',
    'fr-fr': 'Retour en haut',
    'uk-ua': 'Перейти на початок',
    'de-ch': 'Nach oben',
    'ru-ru': 'Перейти наверх',
    'pt-br': 'Voltar ao topo',
  },
  idField: {
    'en-us': 'ID Field',
    'es-es': 'Campo de identificación',
    'fr-fr': "Champ d'identification",
    'ru-ru': 'Поле идентификатора',
    'uk-ua': 'Поле ідентифікатора',
    'de-ch': 'Feld-ID',
    'pt-br': 'Campo de identificação',
  },
  tableUniquenessRules: {
    'en-us': '{tableName:string} Uniqueness Rules',
    'de-ch': '{tableName:string} Eindeutigkeitsregeln',
    'es-es': '{tableName:string} Reglas de unicidad',
    'fr-fr': "{tableName:string} Règles d'unicité",
    'ru-ru': '{tableName:string} Правила уникальности',
    'uk-ua': '{tableName:string} Правила унікальності',
    'pt-br': '{tableName:string} Regras de Unicidade',
  },
  uniquenessRules: {
    'en-us': 'Uniqueness Rules',
    'de-ch': 'Eindeutigkeitsregeln',
    'es-es': 'Reglas de unicidad',
    'fr-fr': "Règles d'unicité",
    'ru-ru': 'Правила уникальности',
    'uk-ua': 'Правила унікальності',
    'pt-br': 'Regras de Unicidade',
  },
  uniqueFields: {
    'en-us': 'Unique Fields',
    'de-ch': 'Einzigartige Bereiche',
    'es-es': 'Campos únicos',
    'fr-fr': 'Champs uniques',
    'ru-ru': 'Уникальные поля',
    'uk-ua': 'Унікальні поля',
    'pt-br': 'Campos únicos',
  },
  addUniquenessRule: {
    'en-us': 'Add Uniqueness Rule',
    'de-ch': 'Eindeutigkeitsregel hinzufügen',
    'es-es': 'Añadir regla de unicidad',
    'fr-fr': "Ajouter une règle d'unicité",
    'ru-ru': 'Добавить правило уникальности',
    'uk-ua': 'Додати правило унікальності',
    'pt-br': 'Adicionar regra de unicidade',
  },
  configureUniquenessRule: {
    'en-us': 'Configure Uniqueness Rule',
    'de-ch': 'Eindeutigkeitsregel konfigurieren',
    'es-es': 'Configurar regla de unicidad',
    'fr-fr': "Configurer la règle d'unicité",
    'ru-ru': 'Настроить правило уникальности',
    'uk-ua': 'Налаштувати правило унікальності',
    'pt-br': 'Configurar regra de unicidade',
  },
  scope: {
    'en-us': 'Scope',
    'es-es': 'Alcance',
    'fr-fr': 'Portée',
    'ru-ru': 'Объем',
    'uk-ua': 'Сфера застосування',
    'de-ch': 'Anwendungsbereich',
    'pt-br': 'Escopo',
  },
  uniquenessDuplicatesFound: {
    'en-us': 'Duplicates found in database',
    'de-ch': 'In der Datenbank gefundene Duplikate',
    'es-es': 'muchos a uno',
    'fr-fr': 'Des doublons ont été trouvés dans la base de données.',
    'ru-ru': 'В базе данных найдены дубликаты',
    'uk-ua': 'У базі даних знайдено дублікати',
    'pt-br': 'Foram encontrados duplicados no banco de dados.',
  },
  exportDuplicates: {
    'en-us': 'Export Duplicates',
    'de-ch': 'Duplikate exportieren',
    'es-es': 'Exportar duplicados',
    'fr-fr': 'Exporter les doublons',
    'ru-ru': 'Экспортировать дубликаты',
    'uk-ua': 'Експорт дублікатів',
    'pt-br': 'Exportar duplicados',
  },
  numberOfDuplicates: {
    'en-us': 'Number of Duplicates',
    'de-ch': 'Anzahl Duplikate',
    'es-es': 'Número de duplicados',
    'fr-fr': 'Nombre de doublons',
    'ru-ru': 'Количество дубликатов',
    'uk-ua': 'Кількість дублікатів',
    'pt-br': 'Número de duplicados',
  },
  schemaViewTitle: {
    'en-us': 'Schema Config: {tableName:string}',
    'es-es': 'Configuración del esquema: {tableName:string}',
    'fr-fr': 'Configuration du schéma : {tableName:string}',
    'ru-ru': 'Конфигурация схемы: {tableName:string}',
    'uk-ua': 'Конфігурація схеми: {tableName:string}',
    'de-ch': 'Schema-Konfiguration: {tableName:string}',
    'pt-br': 'Configuração do esquema: {tableName:string}',
  },
  schemaExportFileName: {
    'en-us': 'Specify 7 Data Model',
    'ru-ru': 'Укажите 7 моделей данных',
    'es-es': 'Especifique el modelo de datos 7',
    'fr-fr': 'Spécifiez le modèle de données 7',
    'uk-ua': 'Specify 7 моделей даних',
    'de-ch': 'Specify 7 Datenmodell',
    'pt-br': 'Especifique 7 modelos de dados',
  },
  sortByHiddenFields: {
    'en-us': 'Sort by hidden fields',
    'de-ch': 'Nach ausgeblendeten Feldern sortieren',
    'es-es': 'Ordenar por campos ocultos',
    'fr-fr': 'Trier par champs cachés',
    'ru-ru': 'Сортировать по скрытым полям',
    'uk-ua': 'Сортувати за прихованими полями',
    'pt-br': 'Ordenar por campos ocultos',
  },
  hidden: {
    'en-us': 'hidden',
    'de-ch': 'versteckt',
    'es-es': 'oculto',
    'fr-fr': 'caché',
    'ru-ru': 'скрытый',
    'uk-ua': 'прихований',
    'pt-br': 'escondido',
  },
  customFieldFormat: {
    'en-us': 'Custom Field Format',
    'de-ch': 'Benutzerdefiniertes Feldformat',
    'es-es': 'Formato de campo personalizado',
    'fr-fr': 'Format de champ personnalisé',
    'ru-ru': 'Пользовательский формат поля',
    'uk-ua': 'Формат користувацького поля',
    'pt-br': 'Formato de campo personalizado',
  },
} as const);
